// Shared feedback for the portal's screens: a load failure with a retry, a not-found, and a section heading with an
// optional "see all" link.

import { useTranslation } from '@clavian/i18n/react';
import { Button, EmptyState, Skeleton } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useProblem } from '../api/use-problem';

export function LoadFailure({
  title,
  description,
  error,
  onRetry,
}: {
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  /** What failed: the body then says what the server's problem code says, and the generic line only for a code with no copy. */
  readonly error?: unknown;
  readonly onRetry?: () => void;
}): ReactNode {
  const { t } = useTranslation('player');
  const problem = useProblem();
  const plain = description ?? t('errors.loadBody');
  return (
    <EmptyState
      title={title ?? t('errors.loadTitle')}
      description={error === undefined ? plain : problem.text(error, typeof plain === 'string' ? { fallback: plain } : {})}
      action={onRetry ? <Button onClick={onRetry}>{t('errors.retry')}</Button> : undefined}
    />
  );
}

/** A loading region: it says so to assistive tech (the skeletons inside are decorative) and keeps its shape while it waits. */
export function LoadingRegion({ children, className }: { readonly children: ReactNode; readonly className?: string }): ReactNode {
  const { t } = useTranslation('player');
  return (
    <div className={className} role="status" aria-busy="true" aria-label={t('app.loading')}>
      {children}
    </div>
  );
}

/** The rows of a list while it loads, the height of a real row so nothing jumps when they land. */
export function RowsSkeleton({ count = 4 }: { readonly count?: number }): ReactNode {
  return (
    <LoadingRegion className="pw-skeleton-rows">
      {Array.from({ length: count }, (_, key) => (
        <Skeleton key={key} height="3.25rem" />
      ))}
    </LoadingRegion>
  );
}

export function NotFound(): ReactNode {
  const { t } = useTranslation('player');
  return (
    <div className="pw-page pw-page--center">
      <EmptyState
        title={t('errors.notFoundTitle')}
        description={t('errors.notFoundBody')}
        action={
          <Link to="/" className="pw-btn pw-btn--primary">
            {t('errors.notFoundHome')}
          </Link>
        }
      />
    </div>
  );
}

/** A section's heading row: the title, and a quiet link to the whole list. */
export function SectionHead({
  id,
  title,
  action,
}: {
  readonly id?: string;
  readonly title: ReactNode;
  readonly action?: ReactNode;
}): ReactNode {
  return (
    <div className="pw-section-head">
      <h2 className="pw-section-title" id={id}>
        {title}
      </h2>
      {action}
    </div>
  );
}
